import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import SEO from '../../components/SEO';

export default function PrivacyPolicyPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Breadcrumb structured data
  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://alliancesecurity.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Privacy Policy",
        "item": "https://alliancesecurity.com/privacy-policy"
      }
    ]
  };

  return (
    <>
      <SEO 
        title="Privacy Policy | Alliance Security Systems"
        description="Alliance Security Systems privacy policy. Learn how we collect, use, and protect your personal information, video surveillance data, and security system information."
        keywords="privacy policy, data protection, security privacy, video surveillance privacy, personal information"
        canonicalUrl="/privacy-policy"
        structuredData={breadcrumbStructuredData}
      />
      
      <div className="min-h-screen bg-white">
        {/* Navigation */}
        <nav className="bg-white shadow-md sticky top-0 z-50" aria-label="Main navigation">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="flex flex-col leading-none">
                <span className="text-2xl sm:text-3xl font-extrabold tracking-wider text-[#D4A853]" style={{ fontFamily: "'Inter', sans-serif" }}>ALLIANCE</span>
                <span className="text-[10px] sm:text-xs font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
              </div>
            </Link>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden w-10 h-10 flex items-center justify-center text-gray-700 cursor-pointer">
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
            <div className="hidden md:flex items-center gap-6">
              <Link to="/" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">Home</Link>
              <Link to="/clients" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">Clients</Link>
              <a href="tel:18884589181" className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer whitespace-nowrap"><i className="ri-phone-line"></i>1-888-458-9181</a>
              <Link to="/#contact" className="bg-[#D4A853] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap">Contact Us</Link>
            </div>
          </div>
          {mobileMenuOpen && (
            <div className="md:hidden bg-white border-t border-gray-100 px-4 pb-4 space-y-2">
              <Link to="/" className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">Home</Link>
              <Link to="/clients" className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">Clients</Link>
              <a href="tel:18884589181" className="flex items-center gap-2 py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50"><i className="ri-phone-line"></i>1-888-458-9181</a>
              <Link to="/#contact" className="block w-full text-center bg-[#D4A853] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap mt-2">Contact Us</Link>
            </div>
          )}
        </nav>

        {/* Hero Section */}
        <section className="py-10 sm:py-16 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs sm:text-sm text-gray-500 mb-4 sm:mb-6">
              <Link to="/" className="hover:text-[#D4A853] cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-[#D4A853]">Privacy Policy</span>
            </nav>
            <h1 className="text-3xl sm:text-5xl font-bold text-[#0A1628] mb-3 sm:mb-4">Privacy Policy</h1>
            <p className="text-gray-600 text-sm sm:text-base">Last Updated: January 15, 2025</p>
          </div>
        </section>

        {/* Content Section */}
        <main>
          <article className="py-10 sm:py-16">
            <div className="max-w-4xl mx-auto px-4 sm:px-6">
              <div className="prose prose-sm sm:prose-lg max-w-none">
                {/* Introduction */}
                <div className="mb-12">
                  <p className="text-gray-600 leading-relaxed text-lg">
                    At Security Solutions, we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, use our services, or interact with our security systems.
                  </p>
                </div>

                {/* Section 1 */}
                <section className="mb-10" aria-labelledby="section-1">
                  <h2 id="section-1" className="text-2xl font-bold text-[#0A1628] mb-4 flex items-center gap-3">
                    <span className="w-10 h-10 bg-[#D4A853]/10 rounded-full flex items-center justify-center text-[#D4A853] text-lg font-bold">1</span>
                    Information We Collect
                  </h2>
                  <div className="pl-13 space-y-4">
                    <h3 className="text-lg font-semibold text-[#1E293B]">Personal Information</h3>
                    <p className="text-gray-600 leading-relaxed">
                      We may collect personal information that you voluntarily provide to us when you:
                    </p>
                    <ul className="list-disc pl-6 text-gray-600 space-y-2">
                      <li>Request a security consultation or quote</li>
                      <li>Schedule an installation or service appointment</li>
                      <li>Create an account for our mobile app or monitoring services</li>
                      <li>Subscribe to our newsletter or marketing communications</li>
                      <li>Contact us with inquiries or support requests</li>
                    </ul>
                    <p className="text-gray-600 leading-relaxed">
                      This information may include your name, email address, phone number, physical address, property details, and payment information.
                    </p>

                    <h3 className="text-lg font-semibold text-[#1E293B] mt-6">Automatically Collected Information</h3>
                    <p className="text-gray-600 leading-relaxed">
                      When you access our website or use our services, we may automatically collect certain information, including:
                    </p>
                    <ul className="list-disc pl-6 text-gray-600 space-y-2">
                      <li>Device information (browser type, operating system, device identifiers)</li>
                      <li>IP address and geographic location</li>
                      <li>Website usage data (pages visited, time spent, click patterns)</li>
                      <li>Security system activity logs and event data</li>
                    </ul>
                  </div>
                </section>

                {/* Section 2 */}
                <section className="mb-10" aria-labelledby="section-2">
                  <h2 id="section-2" className="text-2xl font-bold text-[#0A1628] mb-4 flex items-center gap-3">
                    <span className="w-10 h-10 bg-[#D4A853]/10 rounded-full flex items-center justify-center text-[#D4A853] text-lg font-bold">2</span>
                    How We Use Your Information
                  </h2>
                  <div className="pl-13">
                    <p className="text-gray-600 leading-relaxed mb-4">
                      We use the information we collect for various purposes, including:
                    </p>
                    <ul className="list-disc pl-6 text-gray-600 space-y-2">
                      <li>Providing, maintaining, and improving our security services</li>
                      <li>Processing transactions and sending related information</li>
                      <li>Responding to your inquiries and providing customer support</li>
                      <li>Monitoring and protecting your property through our security systems</li>
                      <li>Sending promotional communications (with your consent)</li>
                      <li>Analyzing usage patterns to enhance our website and services</li>
                      <li>Complying with legal obligations and protecting our rights</li>
                      <li>Dispatching emergency services when alarm events occur</li>
                    </ul>
                  </div>
                </section>

                {/* Section 3 */}
                <section className="mb-10" aria-labelledby="section-3">
                  <h2 id="section-3" className="text-2xl font-bold text-[#0A1628] mb-4 flex items-center gap-3">
                    <span className="w-10 h-10 bg-[#D4A853]/10 rounded-full flex items-center justify-center text-[#D4A853] text-lg font-bold">3</span>
                    Video Surveillance and Recording
                  </h2>
                  <div className="pl-13">
                    <p className="text-gray-600 leading-relaxed mb-4">
                      As a security company providing video surveillance services, we want to be transparent about how video data is handled:
                    </p>
                    <ul className="list-disc pl-6 text-gray-600 space-y-2">
                      <li>Video footage from your security cameras is stored securely in encrypted cloud storage</li>
                      <li>Access to your video recordings is restricted to authorized personnel and yourself</li>
                      <li>Video data is retained according to your service plan (typically 30-90 days)</li>
                      <li>We do not sell or share your video footage with third parties except as required by law or with your explicit consent</li>
                      <li>Our monitoring center may view live feeds only during alarm events or at your request</li>
                    </ul>
                  </div>
                </section>

                {/* Section 4 */}
                <section className="mb-10" aria-labelledby="section-4">
                  <h2 id="section-4" className="text-2xl font-bold text-[#0A1628] mb-4 flex items-center gap-3">
                    <span className="w-10 h-10 bg-[#D4A853]/10 rounded-full flex items-center justify-center text-[#D4A853] text-lg font-bold">4</span>
                    Information Sharing and Disclosure
                  </h2>
                  <div className="pl-13">
                    <p className="text-gray-600 leading-relaxed mb-4">
                      We may share your information in the following circumstances:
                    </p>
                    <ul className="list-disc pl-6 text-gray-600 space-y-2">
                      <li><strong>Emergency Services:</strong> We share necessary information with police, fire, or medical services when responding to alarm events</li>
                      <li><strong>Service Providers:</strong> We work with trusted third-party vendors who assist in operating our business (payment processors, cloud storage providers)</li>
                      <li><strong>Legal Requirements:</strong> We may disclose information when required by law, court order, or government request</li>
                      <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred</li>
                      <li><strong>With Your Consent:</strong> We may share information for other purposes with your explicit permission</li>
                    </ul>
                  </div>
                </section>

                {/* Section 5 */}
                <section className="mb-10" aria-labelledby="section-5">
                  <h2 id="section-5" className="text-2xl font-bold text-[#0A1628] mb-4 flex items-center gap-3">
                    <span className="w-10 h-10 bg-[#D4A853]/10 rounded-full flex items-center justify-center text-[#D4A853] text-lg font-bold">5</span>
                    Data Security
                  </h2>
                  <div className="pl-13">
                    <p className="text-gray-600 leading-relaxed mb-4">
                      We implement robust security measures to protect your personal information:
                    </p>
                    <ul className="list-disc pl-6 text-gray-600 space-y-2">
                      <li>256-bit SSL/TLS encryption for all data transmissions</li>
                      <li>Encrypted storage for sensitive data and video recordings</li>
                      <li>Multi-factor authentication for account access</li>
                      <li>Regular security audits and vulnerability assessments</li>
                      <li>Employee training on data protection and privacy practices</li>
                      <li>Physical security measures at our monitoring centers</li>
                    </ul>
                    <p className="text-gray-600 leading-relaxed mt-4">
                      While we strive to protect your information, no method of transmission over the Internet is 100% secure. We cannot guarantee absolute security but are committed to maintaining industry-leading protection standards.
                    </p>
                  </div>
                </section>

                {/* Section 6 */}
                <section className="mb-10" aria-labelledby="section-6">
                  <h2 id="section-6" className="text-2xl font-bold text-[#0A1628] mb-4 flex items-center gap-3">
                    <span className="w-10 h-10 bg-[#D4A853]/10 rounded-full flex items-center justify-center text-[#D4A853] text-lg font-bold">6</span>
                    Your Rights and Choices
                  </h2>
                  <div className="pl-13">
                    <p className="text-gray-600 leading-relaxed mb-4">
                      You have certain rights regarding your personal information:
                    </p>
                    <ul className="list-disc pl-6 text-gray-600 space-y-2">
                      <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                      <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                      <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal retention requirements)</li>
                      <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications at any time</li>
                      <li><strong>Data Portability:</strong> Request your data in a portable format</li>
                      <li><strong>Withdraw Consent:</strong> Withdraw consent for data processing where applicable</li>
                    </ul>
                    <p className="text-gray-600 leading-relaxed mt-4">
                      To exercise these rights, please contact us using the information provided below.
                    </p>
                  </div>
                </section>

                {/* Section 7 */}
                <section className="mb-10" aria-labelledby="section-7">
                  <h2 id="section-7" className="text-2xl font-bold text-[#0A1628] mb-4 flex items-center gap-3">
                    <span className="w-10 h-10 bg-[#D4A853]/10 rounded-full flex items-center justify-center text-[#D4A853] text-lg font-bold">7</span>
                    Cookies and Tracking Technologies
                  </h2>
                  <div className="pl-13">
                    <p className="text-gray-600 leading-relaxed mb-4">
                      Our website uses cookies and similar tracking technologies to enhance your experience:
                    </p>
                    <ul className="list-disc pl-6 text-gray-600 space-y-2">
                      <li><strong>Essential Cookies:</strong> Required for website functionality and security</li>
                      <li><strong>Analytics Cookies:</strong> Help us understand how visitors interact with our website</li>
                      <li><strong>Marketing Cookies:</strong> Used to deliver relevant advertisements (with your consent)</li>
                    </ul>
                    <p className="text-gray-600 leading-relaxed mt-4">
                      You can manage cookie preferences through your browser settings. Note that disabling certain cookies may affect website functionality.
                    </p>
                  </div>
                </section>

                {/* Section 8 */}
                <section className="mb-10" aria-labelledby="section-8">
                  <h2 id="section-8" className="text-2xl font-bold text-[#0A1628] mb-4 flex items-center gap-3">
                    <span className="w-10 h-10 bg-[#D4A853]/10 rounded-full flex items-center justify-center text-[#D4A853] text-lg font-bold">8</span>
                    Children's Privacy
                  </h2>
                  <div className="pl-13">
                    <p className="text-gray-600 leading-relaxed">
                      Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have inadvertently collected information from a child, please contact us immediately so we can delete it.
                    </p>
                  </div>
                </section>

                {/* Section 9 */}
                <section className="mb-10" aria-labelledby="section-9">
                  <h2 id="section-9" className="text-2xl font-bold text-[#0A1628] mb-4 flex items-center gap-3">
                    <span className="w-10 h-10 bg-[#D4A853]/10 rounded-full flex items-center justify-center text-[#D4A853] text-lg font-bold">9</span>
                    Changes to This Policy
                  </h2>
                  <div className="pl-13">
                    <p className="text-gray-600 leading-relaxed">
                      We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of any material changes by posting the updated policy on our website and updating the "Last Updated" date. We encourage you to review this policy periodically.
                    </p>
                  </div>
                </section>

                {/* Section 10 */}
                <section className="mb-10" aria-labelledby="section-10">
                  <h2 id="section-10" className="text-2xl font-bold text-[#0A1628] mb-4 flex items-center gap-3">
                    <span className="w-10 h-10 bg-[#D4A853]/10 rounded-full flex items-center justify-center text-[#D4A853] text-lg font-bold">10</span>
                    Contact Us
                  </h2>
                  <div className="pl-13">
                    <p className="text-gray-600 leading-relaxed mb-6">
                      If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
                    </p>
                    <div className="bg-gray-50 rounded-2xl p-8 space-y-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-[#D4A853]/10 rounded-full flex items-center justify-center">
                          <i className="ri-building-line text-xl text-[#D4A853]"></i>
                        </div>
                        <div>
                          <p className="font-semibold text-[#0A1628]">Security Solutions</p>
                          <p className="text-gray-600 text-sm">Privacy Department</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-[#D4A853]/10 rounded-full flex items-center justify-center">
                          <i className="ri-mail-line text-xl text-[#D4A853]"></i>
                        </div>
                        <div>
                          <p className="text-gray-600">privacy@securitysolutions.com</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-[#D4A853]/10 rounded-full flex items-center justify-center">
                          <i className="ri-phone-line text-xl text-[#D4A853]"></i>
                        </div>
                        <div>
                          <a href="tel:18884589181" className="text-gray-600 hover:text-[#D4A853] transition-colors cursor-pointer">1-888-458-9181</a>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </article>
        </main>

        {/* Footer */}
        <footer className="bg-[#050B14] text-white py-8 sm:py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6">
              <Link to="/" className="flex items-center gap-3"><div className="flex flex-col leading-none"><span className="text-xl sm:text-2xl font-extrabold tracking-wider text-[#D4A853]" style={{ fontFamily: "'Inter', sans-serif" }}>ALLIANCE</span><span className="text-[8px] sm:text-[10px] font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span></div></Link>
              <p className="text-white/60 text-xs sm:text-sm">© 2024 Security Solutions. All rights reserved.</p>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">Powered by Readdy</a>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
